import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  FLOOR_PLAN_LIMITS,
  collectProviders,
  placementId,
  ratePoint,
  type Beacon,
  type FloorPlan,
  type LocationNode,
  type MeasuredCell,
  type MeasurementSeries,
  type MeasurementSeriesDetailResponse,
  type PlanPlacement,
  type PlanPoint,
} from '@oscom/shared';
import { getCustomer } from '../api/customers';
import {
  deleteFloorPlan,
  deletePlacement,
  floorPlanImageUrl,
  listFloorPlans,
  listPlacements,
  saveFloorPlan,
  savePlacement,
} from '../api/floor-plans';
import { listLocations } from '../api/locations';
import { getMeasurementSeries, listMeasurementSeries } from '../api/measurements';
import { listTechnicalObjects } from '../api/technical-objects';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { FloorPlanUploadDialog } from '../components/FloorPlanUploadDialog';
import { Layout } from '../components/Layout';
import { PlanCanvas, type PlanCanvasHandle, type PlanMarker } from '../components/PlanCanvas';
import { PlanMarkerInfo, type PlanMarkerInfoContent } from '../components/PlanMarkerInfo';
import { RatingLegend } from '../components/RatingLegend';
import { dbmText } from '../components/RatingMatrix';
import { UnplacedList } from '../components/UnplacedList';
import { entryKey, unplacedEntries, type UnplacedEntry } from '../plans/edit';
import { locationPath } from '../plans/locationPath';
import { markerLevel, ratingClass, ratingCombos, type RatingCombo } from '../plans/markers';
import { NotFoundPage } from './NotFoundPage';

/** Newest first: measurement date, then creation time. */
function newestFirst(series: readonly MeasurementSeries[]): MeasurementSeries[] {
  return [...series].sort(
    (a, b) =>
      b.measuredOn.localeCompare(a.measuredOn) ||
      String(b.createdAt).localeCompare(String(a.createdAt)),
  );
}

/** `2026-01-15` -> `15.01.2026`; anything else is shown unchanged. */
function formatDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : isoDate;
}

/** All cells of one point (a point may have several measurements); undefined = none. */
function cellsByPoint(detail: MeasurementSeriesDetailResponse): Map<string, MeasuredCell[]> {
  const result = new Map<string, MeasuredCell[]>();
  for (const entry of detail.measurements) {
    const list = result.get(entry.measurementPointId) ?? [];
    list.push(...entry.cells);
    result.set(entry.measurementPointId, list);
  }
  return result;
}

function pointTitle(
  sequenceNumber: number,
  cells: readonly MeasuredCell[] | undefined,
  combo: RatingCombo | undefined,
): string {
  const rating =
    cells && combo ? ratePoint(cells, [combo.technology], [combo.provider])[0] : undefined;
  if (!combo || !rating || rating.rsrp === null || rating.level === null) {
    return `Punkt ${sequenceNumber}: kein Wert`;
  }
  return `Punkt ${sequenceNumber}: Stufe ${rating.level}, ${dbmText(rating.rsrp)} (${combo.label})`;
}

interface Selection {
  key: string;
  screen: PlanPoint;
}

/** Status line of the edit mode: every action saves at once. */
type SaveState = { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string };

/**
 * One floor plan with its placed measurement points (traffic light of one
 * provider + technology) and beacons. Points need the `measurements` switch,
 * beacons the `technicalObjects` switch (deploy skew: absent reads as off).
 *
 * Edit mode ("Bearbeiten", `measurements:write` or `objects:write`): place
 * objects from the side list, drag markers, take them off the plan. Saves run
 * one after another; a failure (also an etag conflict) shows the API message
 * and reloads the placements, so the markers return to the saved state.
 */
export function FloorPlanViewerPage(): ReactNode {
  const { session } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { customerId: customerIdParam, planId } = useParams<{
    customerId: string;
    planId: string;
  }>();
  const features = session?.status === 'authenticated' ? session.me.features : undefined;
  const enabled = features?.floorPlans === true;
  const measurementsOn = features?.measurements === true;
  const beaconsOn = features?.technicalObjects === true;

  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [plan, setPlan] = useState<FloorPlan | undefined>(undefined);
  const [planMissing, setPlanMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [optionalNotices, setOptionalNotices] = useState<string[]>([]);
  const [seriesList, setSeriesList] = useState<MeasurementSeries[] | undefined>(undefined);
  const [seriesId, setSeriesId] = useState<string | undefined>(undefined);
  const [beacons, setBeacons] = useState<Beacon[]>([]);
  const [detail, setDetail] = useState<MeasurementSeriesDetailResponse | undefined>(undefined);
  const [placements, setPlacements] = useState<PlanPlacement[]>([]);
  const [placementError, setPlacementError] = useState<string | undefined>(undefined);
  const [comboLabel, setComboLabel] = useState<string | undefined>(undefined);
  const [showBeacons, setShowBeacons] = useState(true);
  const [selection, setSelection] = useState<Selection | undefined>(undefined);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const [nameDraft, setNameDraft] = useState<string | undefined>(undefined);
  const [replacing, setReplacing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  // `?bearbeiten=1` opens the plan in edit mode (only with a write right).
  const [editing, setEditing] = useState(() => searchParams.get('bearbeiten') === '1');
  const [placingEntry, setPlacingEntry] = useState<UnplacedEntry | undefined>(undefined);
  const [saveState, setSaveState] = useState<SaveState | undefined>(undefined);
  const pendingSaves = useRef(0);
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  // Etags returned by our own saves since the last load: a second move of the
  // same marker must not send the etag the first move already replaced.
  const savedEtags = useRef(new Map<string, string>());
  // Bumped on every failed save: actions queued before it are dropped.
  const queueGeneration = useRef(0);
  // Started and last settled placement load (see loadPlacements).
  const loadGeneration = useRef(0);
  const loadedGeneration = useRef(0);
  const canvas = useRef<PlanCanvasHandle>(null);

  // Plan, customer, locations and the selectable objects.
  useEffect(() => {
    if (!customerIdParam || !planId || !enabled) return;
    let cancelled = false;
    // Series and beacons are optional: if one list fails, the plan still
    // renders, the failed list counts as empty and a notice names it.
    Promise.all([
      getCustomer(customerIdParam),
      listLocations(customerIdParam),
      listFloorPlans(customerIdParam),
      Promise.allSettled([
        measurementsOn ? listMeasurementSeries(customerIdParam) : undefined,
        beaconsOn ? listTechnicalObjects(customerIdParam) : undefined,
      ]),
    ])
      .then(([customer, locationResponse, planResponse, [seriesResult, objectResult]]) => {
        if (cancelled) return;
        setCustomerName(customer.name);
        setLocations(locationResponse.locations);
        const found = planResponse.plans.find((entry) => entry.id === planId);
        setPlan(found);
        setPlanMissing(found === undefined);
        const seriesResponse = seriesResult.status === 'fulfilled' ? seriesResult.value : undefined;
        const objectResponse = objectResult.status === 'fulfilled' ? objectResult.value : undefined;
        const sorted = newestFirst(seriesResponse?.series ?? []);
        setSeriesList(sorted);
        setSeriesId(sorted[0]?.id);
        setBeacons(objectResponse?.objects.filter((entry) => entry.objectType === 'beacon') ?? []);
        setOptionalNotices(
          [
            seriesResult.status === 'rejected' ? 'Messreihen konnten nicht geladen werden.' : '',
            objectResult.status === 'rejected' ? 'Beacons konnten nicht geladen werden.' : '',
          ].filter((text) => text !== ''),
        );
        setLoadError(undefined);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setLoadError(
          caught instanceof Error ? caught.message : 'Plan konnte nicht geladen werden.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [customerIdParam, planId, enabled, measurementsOn, beaconsOn]);

  // Placements (beacons always, points of the chosen series) and that series.
  // Only the newest load applies: every start (and every effect cleanup) bumps
  // the generation, so an older response can never overwrite a newer state.
  const loadPlacements = useCallback((): Promise<void> => {
    if (!customerIdParam || !planId) return Promise.resolve();
    loadGeneration.current += 1;
    const generation = loadGeneration.current;
    const activeSeries = measurementsOn ? seriesId : undefined;
    return Promise.all([
      listPlacements(customerIdParam, planId, activeSeries),
      activeSeries ? getMeasurementSeries(customerIdParam, activeSeries) : undefined,
    ])
      .then(([placementResponse, seriesDetail]) => {
        if (generation !== loadGeneration.current) return;
        savedEtags.current.clear();
        setPlacements(placementResponse.placements);
        setDetail(seriesDetail);
        setPlacementError(undefined);
      })
      .catch((caught: unknown) => {
        if (generation !== loadGeneration.current) return;
        setPlacementError(
          caught instanceof Error ? caught.message : 'Platzierungen konnten nicht geladen werden.',
        );
      })
      .finally(() => {
        if (generation === loadGeneration.current) loadedGeneration.current = generation;
      });
  }, [customerIdParam, planId, measurementsOn, seriesId]);

  // Saves run later (queued); they always reload with the current series.
  const latestLoad = useRef(loadPlacements);
  useEffect(() => {
    latestLoad.current = loadPlacements;
  }, [loadPlacements]);

  const planFound = plan !== undefined;
  const seriesReady = seriesList !== undefined;
  useEffect(() => {
    if (!planFound || !seriesReady) return;
    setSelection(undefined);
    void loadPlacements();
    return () => {
      // Drops this load and any load a save started meanwhile.
      loadGeneration.current += 1;
    };
  }, [planFound, seriesReady, loadPlacements]);

  // Latest placements for queued saves (etag at run time, not at enqueue time).
  const latestPlacements = useRef(placements);
  useEffect(() => {
    latestPlacements.current = placements;
  }, [placements]);

  const closeInfo = useCallback(() => setSelection(undefined), []);

  if (session?.status !== 'authenticated') return null;
  if (!enabled || planMissing) return <NotFoundPage />;
  if (!customerIdParam || !planId) return null;
  const { me } = session;
  const customerId = customerIdParam;
  const canWrite =
    me.permissions.includes('customers:write') && me.permissions.includes('assets:write');
  const canPlacePoints = measurementsOn && me.permissions.includes('measurements:write');
  const canPlaceBeacons = beaconsOn && me.permissions.includes('objects:write');
  const canEdit = canPlacePoints || canPlaceBeacons;
  const editMode = editing && canEdit;
  const mayPlace = (refType: PlanPlacement['refType']): boolean =>
    refType === 'measurementPoint' ? canPlacePoints : canPlaceBeacons;
  const listUrl = `/kunden/${encodeURIComponent(customerId)}/plaene`;
  const locationsById = new Map(locations.map((node) => [node.id, node]));

  // --- markers ---------------------------------------------------------------
  const technologies = detail?.series.technologies ?? [];
  const providers = detail ? collectProviders(detail.measurements) : [];
  const combos = ratingCombos(technologies, providers);
  const combo = combos.find((entry) => entry.label === comboLabel) ?? combos[0];
  const cells = detail ? cellsByPoint(detail) : new Map<string, MeasuredCell[]>();
  const pointsById = new Map((detail?.points ?? []).map((point) => [point.id, point]));
  const beaconsById = new Map(beacons.map((beacon) => [beacon.id, beacon]));

  const markers: PlanMarker[] = [];
  for (const placement of placements) {
    if (placement.refType === 'measurementPoint' && measurementsOn) {
      const point = pointsById.get(placement.refId);
      if (!point) continue;
      const own = cells.get(point.id);
      markers.push({
        key: placement.id,
        kind: 'point',
        label: String(point.sequenceNumber),
        x: placement.x,
        y: placement.y,
        className: `marker marker--point ${ratingClass(
          markerLevel(own ? { cells: own } : undefined, combo),
        )}`,
        title: pointTitle(point.sequenceNumber, own, combo),
        movable: canPlacePoints,
      });
    } else if (placement.refType === 'beacon' && beaconsOn && showBeacons) {
      const beacon = beaconsById.get(placement.refId);
      if (!beacon) continue;
      markers.push({
        key: placement.id,
        kind: 'beacon',
        label: beacon.beaconName,
        x: placement.x,
        y: placement.y,
        className: 'marker marker--beacon',
        title: `Beacon ${beacon.beaconName}`,
        movable: canPlaceBeacons,
      });
    }
  }

  // Popover content follows the marker; it disappears with the marker.
  let info: PlanMarkerInfoContent | undefined;
  const selectedPlacement = selection
    ? placements.find((entry) => entry.id === selection.key)
    : undefined;
  if (selection && markers.some((marker) => marker.key === selection.key)) {
    const placement = selectedPlacement;
    const point = placement ? pointsById.get(placement.refId) : undefined;
    const beacon = placement ? beaconsById.get(placement.refId) : undefined;
    if (placement?.refType === 'measurementPoint' && point) {
      info = {
        kind: 'point',
        point,
        ratings: ratePoint(cells.get(point.id) ?? [], technologies, providers),
        technologies,
        providers,
      };
    } else if (placement?.refType === 'beacon' && beacon) {
      info = { kind: 'beacon', beacon };
    }
  }

  // --- edit mode -------------------------------------------------------------
  const entries: UnplacedEntry[] =
    editMode && plan
      ? [
          ...(canPlacePoints
            ? unplacedEntries(
                'measurementPoint',
                detail?.points ?? [],
                plan.locationId,
                placements,
                (point) => `Punkt ${point.sequenceNumber}`,
              )
            : []),
          ...(canPlaceBeacons
            ? unplacedEntries('beacon', beacons, plan.locationId, placements, (b) => b.beaconName)
            : []),
        ]
      : [];
  // An entry that left the list (placed, reloaded) no longer waits.
  const placing =
    placingEntry && entries.some((entry) => entryKey(entry) === entryKey(placingEntry))
      ? placingEntry
      : undefined;

  /**
   * Runs one save after the other; reloads happen inside the queue, so the
   * next save sees their result. A failure shows the API message, drops the
   * actions queued so far (they assumed the failed state) and loads the saved
   * placements again: the markers jump back.
   */
  function persist(action: () => Promise<void>): void {
    const generation = queueGeneration.current;
    pendingSaves.current += 1;
    setSaveState({ kind: 'saving' });
    saveQueue.current = saveQueue.current.then(async () => {
      if (generation !== queueGeneration.current) {
        pendingSaves.current -= 1;
        return;
      }
      try {
        await action();
        // A load started before this save returned shows the old state: load
        // again (the newer load supersedes it).
        if (loadGeneration.current !== loadedGeneration.current) await latestLoad.current();
        pendingSaves.current -= 1;
        if (pendingSaves.current === 0) {
          setSaveState((current) => (current?.kind === 'error' ? current : { kind: 'saved' }));
        }
      } catch (caught: unknown) {
        queueGeneration.current += 1;
        pendingSaves.current -= 1;
        setSaveState({
          kind: 'error',
          message: caught instanceof Error ? caught.message : 'Speichern fehlgeschlagen.',
        });
        await latestLoad.current();
      }
    });
  }

  /** Shows the stored document (inserted when a load removed the optimistic one). */
  function keepSaved(saved: PlanPlacement): void {
    if (saved._etag) savedEtags.current.set(saved.id, saved._etag);
    setPlacements((current) =>
      current.some((entry) => entry.id === saved.id)
        ? current.map((entry) => (entry.id === saved.id ? saved : entry))
        : [...current, saved],
    );
  }

  /** Etag at run time: our own last save, else the latest loaded document. */
  function currentEtag(key: string): string | undefined {
    return (
      savedEtags.current.get(key) ??
      latestPlacements.current.find((entry) => entry.id === key)?._etag
    );
  }

  function placeEntry(entry: UnplacedEntry, relative: PlanPoint): void {
    if (!planId) return;
    const id = placementId(planId, entry.refType, entry.refId);
    // Shown at once; replaced by the stored document when the save returns.
    const optimistic = {
      id,
      docType: 'planPlacement',
      customerId,
      planId,
      refType: entry.refType,
      refId: entry.refId,
      x: relative.x,
      y: relative.y,
    } as PlanPlacement;
    setPlacements((current) => [...current.filter((p) => p.id !== id), optimistic]);
    setPlacingEntry(undefined);
    if (entry.refType === 'beacon') setShowBeacons(true);
    // A point without a location gets the plan's location: reload the series,
    // so the location and the candidates are right.
    const point = entry.refType === 'measurementPoint' ? pointsById.get(entry.refId) : undefined;
    const reloadSeries = point !== undefined && !point.locationId;
    persist(async () => {
      // expectNew: a stale view must not overwrite a point placed elsewhere meanwhile.
      keepSaved(
        await savePlacement(customerId, planId, entry.refType, entry.refId, {
          ...relative,
          expectNew: true,
        }),
      );
      if (reloadSeries) await latestLoad.current();
    });
  }

  function moveMarker(key: string, relative: PlanPoint): void {
    const placement = placements.find((entry) => entry.id === key);
    if (!placement || !planId) return;
    setPlacements((current) =>
      current.map((entry) => (entry.id === key ? { ...entry, ...relative } : entry)),
    );
    persist(async () => {
      const etag = currentEtag(key);
      keepSaved(
        await savePlacement(customerId, planId, placement.refType, placement.refId, {
          ...relative,
          ...(etag ? { ifMatchEtag: etag } : {}),
        }),
      );
    });
  }

  function takeOff(placement: PlanPlacement): void {
    if (!planId) return;
    setSelection(undefined);
    setPlacements((current) => current.filter((entry) => entry.id !== placement.id));
    persist(() => deletePlacement(customerId, planId, placement.refType, placement.refId));
  }

  // --- plan actions ----------------------------------------------------------
  async function rename(current: FloorPlan, draft: string): Promise<void> {
    const name = draft.trim();
    if (name === '' || name.length > FLOOR_PLAN_LIMITS.maxNameLength) {
      setActionError('Bitte einen Namen mit höchstens 100 Zeichen angeben.');
      return;
    }
    setBusy(true);
    setActionError(undefined);
    try {
      const saved = await saveFloorPlan(customerId, current.id, {
        name,
        order: current.order,
        ...(current._etag ? { ifMatchEtag: current._etag } : {}),
      });
      setPlan(saved);
      setNameDraft(undefined);
    } catch (caught: unknown) {
      setActionError(caught instanceof Error ? caught.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(current: FloorPlan): Promise<void> {
    setBusy(true);
    setActionError(undefined);
    try {
      await deleteFloorPlan(customerId, current.id);
      navigate(listUrl);
    } catch (caught: unknown) {
      setActionError(caught instanceof Error ? caught.message : 'Löschen fehlgeschlagen.');
      setBusy(false);
    }
  }

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <CustomerContextNav
            customerId={customerId}
            customerName={customerName}
            features={me.features}
          />
        ) : null}

        {loadError ? (
          <div className="card">
            <p className="notice notice--warning">{loadError}</p>
          </div>
        ) : !plan ? (
          <div className="card">
            <p className="muted" role="status" aria-live="polite">
              Plan wird geladen ...
            </p>
          </div>
        ) : (
          <>
            <div className="card stack">
              <div className="plan-header">
                {nameDraft === undefined ? (
                  <h1>{plan.name}</h1>
                ) : (
                  <form
                    className="plan-header__rename"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void rename(plan, nameDraft);
                    }}
                  >
                    <input
                      type="text"
                      aria-label="Planname"
                      maxLength={FLOOR_PLAN_LIMITS.maxNameLength}
                      value={nameDraft}
                      disabled={busy}
                      onChange={(event) => setNameDraft(event.target.value)}
                    />
                    <button type="submit" className="button button--primary" disabled={busy}>
                      Speichern
                    </button>
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={busy}
                      onClick={() => {
                        setNameDraft(undefined);
                        setActionError(undefined);
                      }}
                    >
                      Abbrechen
                    </button>
                  </form>
                )}
                <p className="muted">{locationPath(plan.locationId, locationsById)}</p>
              </div>

              <div className="plan-toolbar">
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => canvas.current?.fit()}
                >
                  Einpassen
                </button>

                {measurementsOn && seriesList ? (
                  seriesList.length === 0 ? (
                    <span className="muted">Keine Messreihe vorhanden.</span>
                  ) : (
                    <label className="plan-toolbar__field">
                      Messreihe{' '}
                      <select
                        value={seriesId ?? ''}
                        onChange={(event) => setSeriesId(event.target.value)}
                      >
                        {seriesList.map((entry) => (
                          <option key={entry.id} value={entry.id}>
                            {entry.label} ({formatDate(entry.measuredOn)})
                          </option>
                        ))}
                      </select>
                    </label>
                  )
                ) : null}

                {measurementsOn && combos.length > 0 ? (
                  <label className="plan-toolbar__field">
                    Ampel{' '}
                    <select
                      value={combo?.label ?? ''}
                      onChange={(event) => setComboLabel(event.target.value)}
                    >
                      {combos.map((entry) => (
                        <option key={entry.label} value={entry.label}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                {beaconsOn ? (
                  <label className="plan-toolbar__field">
                    <input
                      type="checkbox"
                      checked={showBeacons}
                      onChange={(event) => setShowBeacons(event.target.checked)}
                    />{' '}
                    Beacons zeigen
                  </label>
                ) : null}

                {canEdit ? (
                  <button
                    type="button"
                    className={`button ${editMode ? 'button--primary' : 'button--ghost'}`}
                    aria-pressed={editMode}
                    onClick={() => {
                      setEditing(!editMode);
                      setPlacingEntry(undefined);
                    }}
                  >
                    Bearbeiten
                  </button>
                ) : null}

                {canWrite ? (
                  <span className="plan-toolbar__actions">
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={busy || nameDraft !== undefined}
                      onClick={() => {
                        setNameDraft(plan.name);
                        setActionError(undefined);
                      }}
                    >
                      Umbenennen
                    </button>
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={busy || replacing}
                      onClick={() => setReplacing(true)}
                    >
                      Bild ersetzen
                    </button>
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={busy || confirmingDelete}
                      onClick={() => {
                        setConfirmingDelete(true);
                        setActionError(undefined);
                      }}
                    >
                      Löschen
                    </button>
                  </span>
                ) : null}
              </div>

              {measurementsOn && combos.length > 0 ? <RatingLegend /> : null}

              {canWrite && confirmingDelete ? (
                <div className="notice notice--warning" role="alert">
                  <p>
                    Plan „{plan.name}“ wirklich löschen? Alle Platzierungen auf diesem Plan werden
                    entfernt.
                  </p>
                  <p>
                    <button
                      type="button"
                      className="button button--primary"
                      disabled={busy}
                      onClick={() => void remove(plan)}
                    >
                      Endgültig löschen
                    </button>{' '}
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={busy}
                      onClick={() => setConfirmingDelete(false)}
                    >
                      Abbrechen
                    </button>
                  </p>
                </div>
              ) : null}

              {actionError ? (
                <p className="notice notice--warning notice--multiline" role="alert">
                  {actionError}
                </p>
              ) : null}
              {optionalNotices.map((text) => (
                <p key={text} className="notice notice--warning" role="alert">
                  {text}
                </p>
              ))}
              {placementError ? (
                <p className="notice notice--warning" role="alert">
                  {placementError}
                </p>
              ) : null}
              {saveState?.kind === 'error' ? (
                <p className="notice notice--warning notice--multiline" role="alert">
                  {saveState.message}
                </p>
              ) : editMode ? (
                // Present for the whole edit mode, so screen readers announce changes.
                <p className="muted plan-save-status" role="status" aria-live="polite">
                  {saveState?.kind === 'saving'
                    ? 'Speichert …'
                    : saveState?.kind === 'saved'
                      ? 'Gespeichert'
                      : null}
                </p>
              ) : null}
            </div>

            {canWrite && replacing ? (
              <FloorPlanUploadDialog
                customerId={customerId}
                locations={locations}
                replacing={plan}
                onCancel={() => setReplacing(false)}
                onDone={(saved) => {
                  setPlan(saved);
                  setReplacing(false);
                  setImageFailed(false);
                  // After a new image the markers usually need adjusting.
                  if (canEdit) setEditing(true);
                }}
              />
            ) : null}

            <div className={editMode ? 'plan-workspace plan-workspace--editing' : 'plan-workspace'}>
              <div className="card plan-card">
                {placing ? (
                  <div className="plan-placing" role="status">
                    <span>Tippen Sie auf den Plan, um ‚{placing.label}‘ zu setzen.</span>
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() => setPlacingEntry(undefined)}
                    >
                      Abbrechen
                    </button>
                  </div>
                ) : null}
                {imageFailed ? (
                  <div className="notice notice--warning" role="alert">
                    <p>Der Plan konnte nicht geladen werden.</p>
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() => {
                        setImageFailed(false);
                        setImageKey((current) => current + 1);
                      }}
                    >
                      Erneut laden
                    </button>
                  </div>
                ) : null}
                <PlanCanvas
                  ref={canvas}
                  imageUrl={floorPlanImageUrl(customerId, plan.id, plan.currentImage.assetId)}
                  image={plan.currentImage}
                  alt={plan.name}
                  imageKey={imageKey}
                  markers={markers}
                  onMarkerClick={(key, screen) => setSelection({ key, screen })}
                  onImageError={() => setImageFailed(true)}
                  onViewChange={closeInfo}
                  editable={editMode}
                  placing={placing !== undefined}
                  onPlace={(relative) => {
                    if (placing) placeEntry(placing, relative);
                  }}
                  onMove={moveMarker}
                >
                  {selection && info ? (
                    <PlanMarkerInfo
                      key={selection.key}
                      screen={selection.screen}
                      content={info}
                      onClose={closeInfo}
                      actions={
                        editMode && selectedPlacement && mayPlace(selectedPlacement.refType) ? (
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => takeOff(selectedPlacement)}
                          >
                            Vom Plan nehmen
                          </button>
                        ) : undefined
                      }
                    />
                  ) : null}
                </PlanCanvas>
              </div>
              {editMode ? (
                <UnplacedList
                  entries={entries}
                  selected={placing ? entryKey(placing) : undefined}
                  onSelect={(entry) => {
                    setPlacingEntry(entry);
                    closeInfo();
                  }}
                />
              ) : null}
            </div>
          </>
        )}
      </section>
    </Layout>
  );
}
